'use client';

import { LoadingSpinner, toast } from '@crabs-ai/ds-react';
import { useCallback, useMemo, useState } from 'react';
import {
  DtoContentType,
  DtoContentStatus,
  DtoProjectType,
  DtoProjectContentType,
  DtoExtension,
} from '@/shared/constants';
import {
  useGetGtsContentsBulkV2Api,
  GtsContentDto,
} from '@/shared/apis/gts/get-gts-contents-v2';
import AssetContentItem from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/asset/AssetContentItem';
import { PlayerRef } from '@remotion/player';
import {
  useCurrentStateAsRef,
  useWriteContext,
} from '@/features/remotion-editor/editor/utils/use-context';
import { GiPaperWaterdrop } from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import { addAsset } from '@/features/remotion-editor/editor/assets/add-asset-wkwk';
import { classifyUploadFailure } from '@/shared/utils/upload-error-classifier';
import { showUploadFailureToast } from '@/shared/utils/upload-failure-toast';
import { getZoneBoundaryIndex } from '@/features/remotion-editor/editor/utils/zone-utils';
import { CLIP_TYPES, ClipType } from '@/shared/apis/editor/clip-types';
import { ProjectContentV2 } from '@/shared/apis/project/get-project-contents-v2';
import { usePropertyPanelContext } from '@/features/remotion-editor/editor/contexts/property-panel-context';
import { getExtensionFromEncryptedUrl } from '@/shared/utils/s3-presign-url';

export type GtsAssetContentItemsProps = {
  filter: DtoContentType | null;
  playerRef: React.RefObject<PlayerRef | null>;
  projectId: number;
  onEditImage?: (item: ProjectContentV2) => void;
  onSaveToSpace?: (item: ProjectContentV2) => void;
} & React.HTMLAttributes<HTMLDivElement>;

// 모든 GTS ProjectContentType 목록
const ALL_GTS_CONTENT_TYPES = [
  DtoProjectContentType.BASE,
  DtoProjectContentType.SCENE,
  DtoProjectContentType.MAIN,
  DtoProjectContentType.DETAIL,
  DtoProjectContentType.REVIEW,
  DtoProjectContentType.REFERENCE_IMAGE,
  DtoProjectContentType.STORYBOARD,
  DtoProjectContentType.END,
  DtoProjectContentType.EDITED,
  DtoProjectContentType.MERGED,
  DtoProjectContentType.EXTRACTED,
  DtoProjectContentType.SHOWCASE,
  DtoProjectContentType.CONCEPT,
  DtoProjectContentType.CHAT,
];

/**
 * GTS 프로젝트용 에셋 아이템 목록 (v2.0)
 *
 * ProjectContentType 기반으로 콘텐츠를 조회합니다.
 * ContentType 필터링은 클라이언트에서 처리합니다.
 */
export default function GtsAssetContentItems({
  filter,
  playerRef,
  projectId,
  onEditImage,
  onSaveToSpace,
  ...props
}: GtsAssetContentItemsProps) {
  const t = useTranslations();
  const { setAiVideoModalState } = usePropertyPanelContext();

  // v2.0 bulk API로 모든 타입의 콘텐츠 조회
  const { data, isLoading } = useGetGtsContentsBulkV2Api({
    projectId: projectId,
    types: ALL_GTS_CONTENT_TYPES,
  });

  const timelineWriteContext = useWriteContext();
  const stateAsRef = useCurrentStateAsRef();
  const [processingItem, setProcessingItem] = useState<GtsContentDto | null>(
    null,
  );

  // GtsContentDto를 AssetContentItem과 호환되는 형식으로 변환
  const convertToAssetItem = useCallback((item: GtsContentDto) => {
    // contentType 기반으로 기본 확장자 결정
    const contentTypeExtensionMap: Record<number, DtoExtension> = {
      [DtoContentType.video]: DtoExtension.mp4,
      [DtoContentType.image]: DtoExtension.png,
      [DtoContentType.audio]: DtoExtension.mp3,
    };
    const extension: DtoExtension =
      contentTypeExtensionMap[item.contentType ?? DtoContentType.image] ??
      DtoExtension.png;

    return {
      id: item.id,
      name: item.name ?? `content_${item.id}`,
      type: 1 as const, // content
      createdAt: item.createdAt ?? new Date().toISOString(),
      updatedAt: item.createdAt ?? new Date().toISOString(),
      itemId: item.id,
      publicUrl: item.publicUrl ?? '',
      thumbnailPublicUrl: item.thumbnailPublicUrl ?? undefined,
      contentType: item.contentType ?? DtoContentType.image,
      extension: extension,
      status: DtoContentStatus.active,
      duration: item.duration ?? undefined,
    };
  }, []);

  // 외부 URL인지 확인 (프록시가 필요한 도메인)
  const needsProxy = useCallback((url: string) => {
    try {
      const urlObj = new URL(url);
      const proxyDomains = ['asset.m-gs.kr', 'm-gs.kr', 'gsshop.com'];
      return proxyDomains.some(
        (domain) =>
          urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`),
      );
    } catch {
      return false;
    }
  }, []);

  // 내 컴퓨터에 저장 핸들러
  const handleDownloadToComputer = useCallback(
    async (item: ProjectContentV2) => {
      if (!item.publicUrl) return;

      const baseName = item.name.replace(/\.[^/.]+$/, '');
      const extMap: Record<number, string> = {
        [DtoContentType.image]: '.jpeg',
        [DtoContentType.video]: '.mp4',
        [DtoContentType.audio]: '.mp3',
      };
      const downloadName = `${baseName}${extMap[item.contentType] ?? ''}`;

      toast({
        type: 'success',
        description: t('Editor.asset-item.download-start'),
        toastOptions: { position: 'top-center' },
      });

      try {
        const response = await fetch(item.publicUrl);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(blobUrl);

        toast({
          type: 'success',
          description: t('Editor.asset-item.download-success'),
          toastOptions: { position: 'top-center' },
        });
      } catch {
        toast({
          type: 'error',
          description: t('Editor.asset-item.download-fail'),
          toastOptions: { position: 'top-center' },
        });
      }
    },
    [t],
  );

  // 내 스페이스에 저장 핸들러
  const handleSaveToSpace = useCallback(
    (item: ProjectContentV2) => {
      onSaveToSpace?.(item);
    },
    [onSaveToSpace],
  );

  // AI 동영상 생성 핸들러
  const handleGenerateAiVideo = useCallback(
    (item: ProjectContentV2) => {
      setAiVideoModalState({
        isOpen: true,
        imageUrl: item.publicUrl ?? '',
      });
    },
    [setAiVideoModalState],
  );

  // 아이템 클릭 시 에디터에 추가하는 핸들러
  const handleItemClick = useCallback(
    async (item: GtsContentDto) => {
      if (!item.publicUrl) return;
      setProcessingItem(item);

      try {
        // 외부 URL인 경우 프록시 경유, 아니면 직접 fetch
        const fetchUrl = needsProxy(item.publicUrl)
          ? `/api/image-proxy?url=${encodeURIComponent(item.publicUrl)}`
          : item.publicUrl;

        const response = await fetch(fetchUrl);
        if (!response.ok) {
          throw new Error(`Fetch failed: ${response.status}`);
        }
        const blob = await response.blob();

        const state = stateAsRef.current;
        const tracks = state.undoableState.tracks;
        const fps = state.undoableState.fps;
        const compositionWidth = state.undoableState.compositionWidth;
        const compositionHeight = state.undoableState.compositionHeight;

        const contentType = item.contentType ?? DtoContentType.image;
        const typeMap: Record<DtoContentType, ClipType | null> = {
          [DtoContentType.audio]: CLIP_TYPES.AUDIO,
          [DtoContentType.video]: CLIP_TYPES.VIDEO,
          [DtoContentType.image]: CLIP_TYPES.IMAGE,
          [DtoContentType.font]: null,
          [DtoContentType.document]: null,
        };
        const clipType = typeMap[contentType];
        if (!clipType) {
          throw new Error('clipType is null');
        }

        const zoneBoundaryIndex = getZoneBoundaryIndex(tracks);
        const hasAudioZoneTrack = zoneBoundaryIndex < tracks.length;
        const targetTrackIndexForClickAdd =
          clipType === CLIP_TYPES.AUDIO && hasAudioZoneTrack
            ? zoneBoundaryIndex + 1
            : null;

        // 암호화된 URL을 복호화하여 확장자 추출
        const urlExtension =
          (await getExtensionFromEncryptedUrl(item.publicUrl)) ?? 'png';

        // 에디터에 asset 추가
        await addAsset({
          file: blob,
          contentId: String(item.id),
          filename: item.name ?? `content_${item.id}`,
          clip: {
            clipType: clipType,
            contentPublicUri: item.publicUrl,
            videoStart: 0,
            videoEnd: item.duration ?? undefined,
          },
          timelineWriteContext,
          playerRef,
          dropPosition: null,
          fps,
          compositionWidth,
          compositionHeight,
          tracks,
          projectType: DtoProjectType.generateToShort,
          isAsset: true,
          extension: urlExtension,
          sourceRef:
            'sourceRef' in item
              ? ((item as ProjectContentV2).sourceRef ?? null)
              : null,
          targetTrackIndex: targetTrackIndexForClickAdd,
        });

        toast({
          type: 'success',
          description: t('Editor.asset-panel.asset-add-success'),
          toastOptions: { position: 'top-center' },
        });
      } catch (error) {
        console.error('[GtsAssetContentItems] addAsset failed:', error);
        showUploadFailureToast(
          t,
          classifyUploadFailure(error, item.name ?? `content_${item.id}`),
        );
      } finally {
        setProcessingItem(null);
      }
    },
    [needsProxy, playerRef, stateAsRef, t, timelineWriteContext],
  );

  // 모든 타입의 데이터를 flatten하고 ContentType으로 필터링
  const filteredItems = useMemo(() => {
    if (!data?.data) return [];

    // bulk API 응답은 Record<number, GtsContentDto[]> 형식
    const allItems: GtsContentDto[] = Object.values(data.data).flat();

    // 최신순 정렬
    const sortedItems = allItems.sort((a, b) => {
      const dateA = new Date(a.createdAt ?? 0).getTime();
      const dateB = new Date(b.createdAt ?? 0).getTime();
      return dateB - dateA;
    });

    if (!filter) {
      return sortedItems;
    } else {
      return sortedItems.filter((item) => item.contentType === filter);
    }
  }, [data, filter]);

  if (isLoading)
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );

  return (
    <>
      {filteredItems.length > 0 ? (
        <div
          className="mb-xl grid w-full grid-cols-2 gap-2xs overflow-y-auto"
          {...props}
        >
          {filteredItems.map((item: GtsContentDto) => {
            const assetItem = convertToAssetItem(item);
            const isProcessing = processingItem?.id === item.id;

            return (
              <div
                key={item.id}
                className="relative cursor-pointer"
                onClick={() => !isProcessing && handleItemClick(item)}
              >
                <AssetContentItem
                  item={assetItem}
                  onClick={() => {}}
                  showTypeLabel={filter === null}
                  onSaveToSpace={handleSaveToSpace}
                  onDownloadToComputer={handleDownloadToComputer}
                  onEditImage={onEditImage}
                  onGenerateAiVideo={handleGenerateAiVideo}
                />
                {isProcessing && (
                  <div className="absolute inset-0 flex items-center justify-center rounded bg-black/50">
                    <LoadingSpinner size="small" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-sm">
          <GiPaperWaterdrop className="size-4xl" />
          <div className="flex flex-col items-center gap-4xs">
            {filter === null ? (
              <>
                <p className="text-title-medium">
                  {t('Editor.asset-panel.all-empty-title')}
                </p>
                <p className="text-body-medium">
                  {t('Editor.asset-panel.all-empty-description')}
                </p>
              </>
            ) : (
              <>
                <p className="text-title-medium">
                  {t('Editor.asset-panel.filter-empty-title')}
                </p>
                <p className="text-center text-body-medium">
                  {t('Editor.asset-panel.filter-empty-description')}
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
